// src/pages/Manager/GiveMark.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  Shows the marks for the person's job: the floor's ten for a
//               salesperson, the four management marks for a manager.
//   2026-09-27  New. The sheet a manager gives a mark from. Opens from a face
//               on the floor-this-morning panel.
//
// TEN SECONDS, OR IT WILL NOT HAPPEN.
//
// Recognition fails in dealerships for the same reason one on ones do: it is
// right, everybody agrees, and it loses to the next thing. So this is a tap on
// a face, a tap on what you saw and a button. The note is optional and the
// sheet says so, because a required note is the thing that turns ten seconds
// into "later".
//
// WHAT YOU SAW, NOT HOW GOOD THEY ARE. The marks name behaviours from the
// thirteen steps (content/marks.js). There is no "great job" in the list and
// no free text mark, on purpose.
//
// THE FLOOR SEES THIS, and the sheet says so before anybody types. That line
// does two jobs: it is true (0046 lets the whole store read marks, because
// witnessed recognition is worth more), and it is what keeps the note box from
// becoming somewhere to say what went wrong. It also repeats the customer rule
// at the moment somebody is most likely to type a customer's name.

import { useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Button, Textarea, SimpleGrid, Alert, AlertIcon,
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody, useBreakpointValue,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Check, ChevronRight } from 'lucide-react'
import Medallion from '../../components/ui/Medallion.jsx'
import PersonAvatar from '../Team/PersonAvatar.jsx'
import { marksFor, markByKey } from '../../content/marks'
import { firstNameFor } from '../../lib/names'
import { giveMark } from '../../lib/recognitions'

const NOTE_MAX = 160

export default function GiveMark({ person, isOpen, onClose, dealershipId, givenBy, lastMark, onGiven }) {
  const placement = useBreakpointValue({ base: 'bottom', md: 'right' }) || 'right'
  const [mark, setMark] = useState(null)
  const [note, setNote] = useState('')
  const [state, setState] = useState({ saving: false, error: null, given: null })

  // A fresh sheet for each person. Carrying a half-chosen mark from one face
  // to the next is how the wrong person gets "Trade done right".
  useEffect(() => {
    setMark(null)
    setNote('')
    setState({ saving: false, error: null, given: null })
  }, [person?.id, isOpen])

  if (!person) return null
  const first = firstNameFor(person) || 'them'
  const chosen = mark ? markByKey(mark) : null

  const give = async () => {
    if (!chosen) return
    setState({ saving: true, error: null, given: null })
    const { data, error } = await giveMark({ dealershipId, givenBy, givenTo: person.id, mark: chosen.key, note })
    if (error) {
      setState({ saving: false, error, given: null })
      return
    }
    setState({ saving: false, error: null, given: data })
    onGiven?.(data)
  }

  return (
    <Drawer isOpen={isOpen} placement={placement} onClose={onClose} size="md">
      <DrawerOverlay bg="blackAlpha.500" />
      <DrawerContent
        bg="card"
        borderTopRadius={placement === 'bottom' ? 'cardLg' : 0}
        maxH={placement === 'bottom' ? '92vh' : undefined}
      >
        {/* Solid behind the X, because on a phone the sheet scrolls and a
            bare X ends up floating over the marks grid. */}
        <DrawerCloseButton mt={2} bg="card" borderRadius="full" zIndex={1} _hover={{ bg: 'surface' }} />
        <DrawerBody pt={6} px={{ base: 5, md: 7 }} pb={{ base: 8, md: 8 }}>
          {state.given ? (
            // THE MOMENT, FOR THE GIVER. Short, because they have a floor to
            // run, and it says where the other person will see it so the
            // manager knows it landed somewhere rather than into a table.
            <VStack align="stretch" spacing={5} py={4}>
              <HStack spacing={4}>
                <Medallion mark={state.given.mark} size={64} />
                <Box>
                  <Text fontWeight={600} fontSize="lg" letterSpacing="-0.01em">
                    Given. {markByKey(state.given.mark)?.name}.
                  </Text>
                  <Text fontSize="body-sm" color="inkMuted" lineHeight={1.5}>
                    {first} sees it the next time they open Today.{' '}
                    {person.role === 'manager' || person.role === 'owner'
                      ? 'Tell them in person too, which is where it counts twice.'
                      : 'Say it out loud at the morning meeting too, which is where it counts twice.'}
                  </Text>
                </Box>
              </HStack>
              <HStack spacing={3}>
                <Button onClick={onClose}>Done</Button>
                <Button variant="ghost" onClick={() => setState({ saving: false, error: null, given: null })}>
                  Give another
                </Button>
              </HStack>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={5}>
              <HStack spacing={3} pr={10}>
                <PersonAvatar person={person} size={44} />
                <Box minW={0}>
                  <Text textStyle="eyebrow" color="inkDim">
                    RECOGNISE
                  </Text>
                  <Text fontWeight={600} fontSize={{ base: 'lg', md: 'xl' }} letterSpacing="-0.01em" lineHeight={1.25}>
                    What did you see {first} do?
                  </Text>
                  <Text fontSize="sm" color="inkDim">
                    {lastMark
                      ? `Last recognised ${lastMark.when}: ${markByKey(lastMark.mark)?.name || 'a mark'}.`
                      : 'Not recognised in the last week.'}
                  </Text>
                </Box>
              </HStack>

              <SimpleGrid columns={2} spacing={2} role="radiogroup" aria-label="Which mark">
                {marksFor(person.role).map((m) => {
                  const on = mark === m.key
                  return (
                    <HStack
                      key={m.key}
                      as="button"
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setMark(m.key)}
                      spacing={2.5}
                      px={2.5}
                      py={2}
                      minH="52px"
                      borderRadius="md"
                      border="1px solid"
                      borderColor={on ? 'ink' : 'line'}
                      bg={on ? 'surface' : 'transparent'}
                      boxShadow={on ? '0 0 0 1px var(--chakra-colors-ink)' : 'none'}
                      textAlign="left"
                      transition="border-color 120ms ease, background 120ms ease"
                      _hover={{ borderColor: on ? 'ink' : 'lineStrong' }}
                      _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
                    >
                      <Medallion mark={m} size={34} />
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight={600} lineHeight={1.25}>
                          {m.name}
                        </Text>
                        {m.step && (
                          <Text fontSize="xs" color="inkDim" fontFamily="mono">
                            Step {m.step}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  )
                })}
              </SimpleGrid>

              {chosen && (
                <Text fontSize="body-sm" color="inkMuted" lineHeight={1.5} mt={-1}>
                  {chosen.saw}
                </Text>
              )}

              <Box>
                <HStack justify="space-between" align="baseline" mb={1.5}>
                  <Text fontSize="sm" fontWeight={600}>
                    In your words <Text as="span" color="inkDim" fontWeight={400}>(optional)</Text>
                  </Text>
                  <Text fontSize="xs" color="inkDim" fontFamily="mono">
                    {note.length}/{NOTE_MAX}
                  </Text>
                </HStack>
                <Textarea
                  id="give-mark-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                  rows={2}
                  fontSize="body"
                  placeholder="Walked the trade with them before the numbers and it saved the deal."
                />
                <Text fontSize="xs" color="inkDim" mt={1.5} lineHeight={1.5}>
                  The floor sees this. Keep it about what {first} did, and never name a customer.
                </Text>
              </Box>

              {state.error && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {state.error}
                </Alert>
              )}

              <Flex gap={3} align="center" wrap="wrap">
                <Button
                  onClick={give}
                  isDisabled={!chosen || !dealershipId}
                  isLoading={state.saving}
                  leftIcon={<Check size={15} />}
                >
                  Give the mark
                </Button>
                <Button
                  as={RouterLink}
                  to={`/team/${person.id}/`}
                  variant="ghost"
                  size="sm"
                  rightIcon={<ChevronRight size={14} />}
                >
                  Open {first}'s page
                </Button>
              </Flex>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
